"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

const faqKeys = ["q1", "q2", "q3", "q4", "q5", "q6"] as const;
const faqAnswerKeys = ["a1", "a2", "a3", "a4", "a5", "a6"] as const;

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="border-t border-slate-200 bg-white py-16 sm:py-20 md:py-24">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
        <div className="mb-10 sm:mb-12">
          <h2
            className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
            suppressHydrationWarning
          >
            {t("faq.title")}
          </h2>
          <p
            className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base"
            suppressHydrationWarning
          >
            {t("faq.subtitle")}
          </p>
        </div>

        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {faqKeys.map((qKey, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={qKey}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="group flex w-full min-w-0 items-start justify-between gap-4 py-5 text-left transition-colors hover:bg-slate-50/80 sm:py-[1.125rem]"
                >
                  <span
                    className={`min-w-0 flex-1 text-[15px] font-medium leading-snug tracking-tight sm:text-base ${
                      isOpen ? "text-slate-900" : "text-slate-800"
                    }`}
                    suppressHydrationWarning
                  >
                    {t(`faq.${qKey}`)}
                  </span>
                  <ChevronDown
                    className={`mt-0.5 h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ease-out group-hover:text-slate-600 ${
                      isOpen ? "rotate-180 text-primary" : ""
                    }`}
                    aria-hidden
                  />
                </button>
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p
                      className="pb-5 pr-8 text-sm leading-relaxed text-slate-600 sm:text-[15px] sm:leading-relaxed"
                      suppressHydrationWarning
                    >
                      {t(`faq.${faqAnswerKeys[index]}`)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
